import { GeneratorSwitchButtonTrueIcon } from '@/components/Icons';
import Show from '@/components/Show';
import { GeneratorTypeEnums, R3imagineStatusType, RecordingStatusEnum } from '@/state/generator-store';
import { cn } from '@/utils';
import clsx from 'clsx';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import useAudioRecorder from '../hooks/useAudioRecorder';
import styles from './index.module.scss';
import { Props } from './interfaces';
import { convertToWav } from './utils';

const LiveAudioVisualizer = React.lazy(async () => {
  const { LiveAudioVisualizer } = await import('react-audio-visualize');
  return { default: LiveAudioVisualizer };
});

const defaultCountDown = 3;

const AudioRecorder = ({
  onRecordingComplete,
  onNotAllowedOrFound,
  recorderControls,
  audioTrackConstraints,
  downloadOnSavePress = false,
  downloadFileExtension = 'webm',
  mediaRecorderOptions,
  showVisualizer = false,
  isCountdown,
  setIsCountdown,
  classes,
  disabled,
  recordDisabled = false,
  setRecordingStatus,
  setRecordingTime,
  status,
  generatorType,
}: Props) => {
  const { startRecording, stopRecording, togglePauseResume, recordingBlob, isRecording, isPaused, recordingTime, mediaRecorder } =
    recorderControls ?? useAudioRecorder(audioTrackConstraints, onNotAllowedOrFound, mediaRecorderOptions);

  const audioRecorderRef = useRef<HTMLDivElement>(null);

  const audioVisualizerRef = useRef<HTMLDivElement>(null);

  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const [countdown, setCountdown] = useState<number>(defaultCountDown);

  const [startTime, setStartTime] = useState<number>(0);

  const stopAudioRecorder = async () => {
    const blob = await stopRecording();
    const endTime = new Date().getTime();
    onRecordingComplete?.(blob, endTime - startTime);
    setStartTime(0);
    if (downloadOnSavePress) {
      await downloadBlob(blob);
    }
  };

  useEffect(() => {
    setRecordingTime?.(recordingTime);
  }, [recordingTime, setRecordingTime]);

  const startCountdownAndRecording = () => {
    setCountdown(defaultCountDown);
    setIsTimerRunning(true);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1 || prev === 0) {
          setIsTimerRunning(false);
          clearInterval(timer);
          startRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const downloadBlob = async (blob: Blob): Promise<void> => {
    if (typeof crossOriginIsolated === 'undefined' || !crossOriginIsolated) {
      console.warn(
        `This website is not "cross-origin isolated". Audio will be downloaded in webm format, since wav encoding requires cross origin isolation. Please visit https://web.dev/cross-origin-isolation-guide/ and https://web.dev/coop-coep/ for information on how to make your website "cross-origin isolated"`,
      );
    }

    const downloadBlob = crossOriginIsolated ? await convertToWav(blob) : blob;
    const fileExt = crossOriginIsolated ? 'wav' : 'webm';
    const url = URL.createObjectURL(downloadBlob);

    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `audio.${fileExt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleRecordButtonOnClick = () => {
    if (generatorType === GeneratorTypeEnums.R3IMAGINE) {
      setRecordingStatus?.(RecordingStatusEnum.Recording);
    }
    if (recordDisabled) {
      return;
    }
    if (isRecording) {
      setCountdown(defaultCountDown);
      stopAudioRecorder();
      // setRecordingStatus?.(RecordingStatusEnum.RecordingFinished);
      setIsCountdown?.(false);
    } else {
      setIsCountdown?.(true);
      setRecordingStatus?.(RecordingStatusEnum.Recording);
      const time = new Date().getTime();
      setStartTime(time + defaultCountDown * 1000);
      startCountdownAndRecording();
    }
  };

  const countdownColor = useMemo(() => {
    if (isRecording) {
      return 'text-jen-orange';
    }
    if (isTimerRunning) {
      return 'text-white';
    }
    return 'text-[#DDE1E11A]';
  }, [isRecording, isTimerRunning]);

  const waveWidth = useMemo(() => {
    const audioVisualizerWidth = audioVisualizerRef.current?.offsetWidth;
    return audioVisualizerWidth;
  }, [audioVisualizerRef.current]);

  const RecordButton = () => (
    <div className="bg-gradient-to-br from-[#383A3C] to-[#2B2E30] rounded border border-[#46494c]">
      <button
        className={cn('flex justify-center items-center', styles['record-button'], 'hover:brightness-125', {
          'brightness-90 cursor-not-allowed': isTimerRunning || status === R3imagineStatusType.WaitingAgree || disabled || recordDisabled,
          'opacity-50': disabled || recordDisabled,
        })}
        disabled={isTimerRunning || status === R3imagineStatusType.WaitingAgree || disabled}
        onClick={handleRecordButtonOnClick}
        data-testid="ar_mic"
        type="button"
      >
        <div className={`${styles['button-inner']}`}>
          <div className={`mt-4 ${isRecording ? 'text-white' : ''} ${disabled || recordDisabled ? 'opacity-50' : ''}`}>
            <Show when={!isRecording}>
              <GeneratorSwitchButtonTrueIcon className="w-4 h-4" />
            </Show>
            <Show when={isRecording}>
              <div className="w-4 h-4 bg-white rounded-sm border border-[#46494c]" />
            </Show>
          </div>
          <div className={`mt-1 text-[0.6rem] font-mono ${isRecording ? 'text-white' : 'text-[#9d9da6]'} ${disabled ? 'opacity-50' : ''}`}>
            REC
          </div>
        </div>
      </button>
    </div>
  );

  const RecordingStatus = () => (
    <div className={styles['waiting-recording-time']}>
      <Show when={isRecording}>
        <div className={styles['audio-recorder-status']}>
          <span className={`${styles['status-text']}`}>REC</span>
        </div>
      </Show>
      <Show when={!isRecording}>
        <div className={styles['audio-recorder-status-not-recording']}>
          <span className={styles['status-text']}>REC</span>
        </div>
      </Show>
      <div className={`flex items-center justify-center text-[2rem] ${countdownColor}`}>{countdown}</div>
    </div>
  );

  const AudioVisualizerDefault = useMemo(() => {
    const height = audioVisualizerRef?.current?.clientHeight;
    const width = audioVisualizerRef?.current?.clientWidth;
    if (width && height) {
      const counts = Math.floor(width / 6);
      return Array.from({ length: counts }, (_, index) => (
        <div key={index} className={`w-1 h-[${height - 5}px] opacity-5 ml-[2px] bg-[#d9d9d9] rounded-[1px]`} />
      ));
    }
  }, [audioVisualizerRef?.current?.clientHeight, audioVisualizerRef?.current?.clientWidth]);

  const AudioVisualizer = () => (
    <div className="flex-grow" ref={audioVisualizerRef}>
      <Show when={showVisualizer}>
        <span className={`${styles['audio-recorder-visualizer']} ${!isRecording ? styles['display-none'] : ''} w-full h-full`}>
          <Show when={Boolean(mediaRecorder)} fallback={<div className="w-full h-full flex mt-[-4px]">{AudioVisualizerDefault}</div>}>
            <Suspense fallback={<></>}>
              <LiveAudioVisualizer
                mediaRecorder={mediaRecorder as MediaRecorder}
                barWidth={4}
                gap={2}
                width={waveWidth}
                height={75}
                barColor="orange"
                fftSize={2048}
                maxDecibels={-10}
                minDecibels={-80}
                smoothingTimeConstant={0.4}
              />
            </Suspense>
          </Show>
        </span>
      </Show>
      <Show when={!showVisualizer}>
        <>
          <span
            className={clsx(styles['audio-recorder-status'], !isRecording && styles['display-none'], classes?.AudioRecorderStatusClass)}
          >
            <span className={styles['audio-recorder-status-dot']} />
            Recording
          </span>
        </>
      </Show>
    </div>
  );

  const RecordingTime = () => (
    <div className="h-full flex flex-col justify-center">
      <div className={styles['recording-time']}>
        <div className={`${isRecording ? 'text-jen-orange' : 'text-[#DDE1E11A]'} leading-snug`}>00:</div>
        <div className={`${isRecording ? 'text-jen-orange' : 'text-[#DDE1E11A]'} leading-snug`}>
          {Math.floor(recordingTime / 60)
            .toString()
            .padStart(2, '0')}
          :
        </div>
        <div className={`${isRecording ? 'text-jen-orange' : 'text-[#DDE1E11A]'} leading-snug`}>
          {(recordingTime % 60).toString().padStart(2, '0')}&nbsp;
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex rounded-lg w-full gap-1" data-testid="audio_recorder" ref={audioRecorderRef}>
      <div className="flex items-center justify-center">
        <RecordButton />
      </div>
      <div className="bg-[#383A3C] rounded-md p-1 flex-1">
        <div className="bg-[#191A1B] rounded-md">
          <div className="flex rounded-sm">
            <div className="flex-grow">
              <div className={styles['audio-visualizer']}>
                <Show when={Boolean(disabled)}>
                  <div className="w-full h-full flex px-3 py-3 justify-around items-center">
                    <div className="text-[#ff5b27] w-36 text-[12px] font-normal font-['GT Pressura Mono Trial'] uppercase leading-[14px]">
                      r3imagine is not available on your current plan.
                    </div>
                    <div
                      className="w-32 h-6 px-2.5 py-[5px] bg-[#ff5b27]/40 hover:bg-[#ff5b27]/60 rounded-sm justify-center items-center gap-2.5 inline-flex cursor-pointer pointer-events-auto hover:text-white   transition duration-300"
                      onClick={() => {
                        window.open('/recharge?tab=business', '_blank');
                      }}
                    >
                      <div className="UpgradeNow text-[#ff5b27] text-[12px] font-normal font-['GT Pressura Mono Trial'] uppercase leading-normal transition duration-300">
                        UPGRADE now
                      </div>
                    </div>
                  </div>
                </Show>
                <Show when={!disabled}>
                  <>
                    <RecordingStatus />
                    <AudioVisualizer />
                    <RecordingTime />
                  </>
                </Show>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioRecorder;
